import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import Button, { ButtonVariants } from '../../../atoms/Button';
import InputWrapper from '../../../atoms/InputWrapper';
import FilterGroup, { FilterGroupBackdrop, } from '../../../molecules/filter/FilterGroup';
import Cross from '../../../atoms/icons/Cross';
import { mapValues, uniqueId } from 'lodash';
import { Text } from './inputTypes/Text';
import { SingleSelect } from './inputTypes/SingleSelect';
import { MultiSelect } from './inputTypes/MultiSelect';
import { isKeyOf } from '../../../../helpers/isKeyOf';
import H1 from '../../../atoms/title/H1';
import Magnifier from '../../../atoms/icons/Magnifier';
import Delete from '../../../atoms/icons/Delete';
const StyledCross = styled(Cross) `
    width: 12px;
    height: 12px;
`;
const StyledMagnifier = styled(Magnifier) `
    width: 20px;
    height: 20px;
`;
const StyledDelete = styled(Delete) `
    width: 20px;
    height: 20px;
    fill: white;
`;
const ButtonInputWrapper = styled(InputWrapper) `
    flex-shrink: 0;
`;
const Filters = ({ filters, setFilters, setFilter, availableFilters, }) => {
    const { t } = useTranslation();
    const { filterCanvasVisible, showFilterCanvas, hideFilterCanvas, filterControlId, } = useFilterCanvas();
    useEscButton(hideFilterCanvas);
    const hasActiveFilters = useMemo(() => {
        const filterKeys = Object.keys(filters);
        return filterKeys
            .map(key => [key, filters[key]])
            .some(([key, value]) => {
            if (key === 'status' &&
                value === availableFilters.status?.defaultValue) {
                return false;
            }
            if (Array.isArray(value)) {
                return Boolean(value.length);
            }
            return Boolean(value);
        });
    }, [filters]);
    const clearFilters = () => {
        setFilters(mapValues({ ...filters }, (val, key) => {
            if (Array.isArray(val))
                return [];
            if (key === 'status') {
                return availableFilters.status?.defaultValue || '';
            }
            return '';
        }));
    };
    const allFilters = Object.entries(availableFilters);
    const visibleFilters = allFilters.splice(0, 4);
    const initiallyHiddenFilters = allFilters; // remainder after splice
    return (_jsx(_Fragment, { children: [visibleFilters, initiallyHiddenFilters].map((_filters, index) => {
            return (_jsxs("div", { "aria-haspopup": index === 1, "aria-expanded": filterCanvasVisible, "aria-controls": filterControlId, children: [index === 1 && (_jsx(FilterGroupBackdrop, { onClick: hideFilterCanvas })), _jsxs(FilterGroup, { children: [index === 1 && (_jsx(H1, { children: t('dossier.show_more_filters') })), _filters.map(([name, filter]) => {
                                if (!isKeyOf(name, availableFilters)) {
                                    throw new Error(`Filter ${name} is not a member of availableFilters!`);
                                }
                                switch (filter.type) {
                                    case 'text': {
                                        return (_jsx(Text, { name: name, filters: filters, setFilter: setFilter }, name));
                                    }
                                    case 'select': {
                                        const isMultiple = 'multiple' in filter &&
                                            filter.multiple === true;
                                        if (isMultiple) {
                                            return (_jsx(MultiSelect, { name: name, filters: filters, setFilter: setFilter, options: filter.options, translateLabels: [
                                                    'objection_status',
                                                ].includes(name) }, name));
                                        }
                                        else {
                                            return (_jsx(SingleSelect, { name: name, filters: filters, setFilter: setFilter, options: filter.options, defaultValue: 'defaultValue' in filter
                                                    ? filter.defaultValue
                                                    : undefined, translateLabels: [
                                                    'status',
                                                ].includes(name) }, name));
                                        }
                                    }
                                }
                            }), index === 0 &&
                                Boolean(initiallyHiddenFilters.length) &&
                                filterCanvasVisible === false && (_jsx(ButtonInputWrapper, { children: _jsx(Button, { primary: true, id: filterControlId, icon: _jsx(StyledMagnifier, {}), onClick: showFilterCanvas, children: t('dossier.show_more_filters') }) })), hasActiveFilters && (_jsx(ButtonInputWrapper, { children: _jsx(Button, { variant: ButtonVariants.Red, icon: _jsx(StyledDelete, {}), onClick: () => clearFilters(), children: t('clear_all_filters') }) })), index === 1 && (_jsx(ButtonInputWrapper, { children: _jsx(Button, { secondary: true, id: filterControlId, icon: _jsx(StyledCross, {}), onClick: hideFilterCanvas, children: t('dossier.close_more_filters') }) }))] })] }, index));
        }) }));
};
function useFilterCanvas() {
    const [filterCanvasVisible, setFilterCanvasVisibility] = useState(false);
    const showFilterCanvas = () => setFilterCanvasVisibility(true);
    const hideFilterCanvas = () => setFilterCanvasVisibility(false);
    const filterControlId = useRef(uniqueId());
    return {
        filterCanvasVisible,
        showFilterCanvas,
        hideFilterCanvas,
        filterControlId: filterControlId.current,
    };
}
function useEscButton(callback) {
    const escFunction = useCallback(event => {
        if (event.key === 'Escape') {
            callback();
        }
    }, []);
    useEffect(() => {
        document.addEventListener('keydown', escFunction, false);
        return () => {
            document.removeEventListener('keydown', escFunction, false);
        };
    }, []);
}
export default Filters;
